import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  MoveVertical,
  Palette,
  Radius,
  Type,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { BuilderElementDraft } from "./BuilderSectionElementList";

interface BuilderElementStyleEditorProps {
  element: BuilderElementDraft;
  readOnly?: boolean;
  onChange: (updates: Partial<BuilderElementDraft>) => void;
  className?: string;
}

function getStringStyle(
  element: BuilderElementDraft,
  key: string,
  fallback = ""
) {
  const value = element.style?.[key];
  return typeof value === "string" ? value : fallback;
}

function getNumberStyle(
  element: BuilderElementDraft,
  key: string,
  fallback: number
) {
  const value = element.style?.[key];
  return typeof value === "number" ? value : fallback;
}

function updateStyle(
  element: BuilderElementDraft,
  key: string,
  value: string | number | boolean | null
) {
  return {
    style: {
      ...(element.style ?? {}),
      [key]: value,
    },
  };
}

const ALIGN_OPTIONS = [
  { value: "left", label: "Left", icon: AlignLeft },
  { value: "center", label: "Center", icon: AlignCenter },
  { value: "right", label: "Right", icon: AlignRight },
] as const;

const FONT_WEIGHT_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "medium", label: "Medium" },
  { value: "semibold", label: "Semibold" },
  { value: "bold", label: "Bold" },
] as const;

function SectionCard({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description?: string;
  icon: typeof Palette;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.04] p-2 text-white/65">
            <Icon className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">{title}</p>
            {description ? (
              <p className="mt-1 text-xs leading-5 text-white/40">
                {description}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  hint,
}: {
  label: string;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="block text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function Input({
  value,
  onChange,
  disabled,
  min,
  max,
  placeholder,
  type = "text",
}: {
  value: string | number;
  onChange: (value: string) => void;
  disabled: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
  type?: "text" | "number";
}) {
  return (
    <input
      type={type}
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
    />
  );
}

function Select({
  value,
  onChange,
  disabled,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
    >
      {children}
    </select>
  );
}

export function BuilderElementStyleEditor({
  element,
  readOnly = false,
  onChange,
  className,
}: BuilderElementStyleEditorProps) {
  const textAlign = getStringStyle(element, "textAlign", "left");
  const fontSize = getNumberStyle(element, "fontSize", 16);
  const fontWeight = getStringStyle(element, "fontWeight", "normal");
  const color = getStringStyle(element, "color", "#111827");
  const backgroundColor = getStringStyle(element, "backgroundColor", "");
  const paddingTop = getNumberStyle(element, "paddingTop", 0);
  const paddingBottom = getNumberStyle(element, "paddingBottom", 0);
  const borderRadius = getNumberStyle(element, "borderRadius", 16);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-white/10 via-white/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <Palette className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">Block Style</p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Fine-tune how this block reads on the page stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Typography"
          description="Control alignment, size, and weight for the selected block."
          icon={Type}
        >
          <div>
            <FieldLabel
              label="Text alignment"
              hint="Choose how text should sit inside this block."
            />

            <div className="flex flex-wrap gap-2">
              {ALIGN_OPTIONS.map((option) => {
                const Icon = option.icon;
                const active = textAlign === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={readOnly}
                    onClick={() =>
                      onChange(updateStyle(element, "textAlign", option.value))
                    }
                    className={cn(
                      "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs transition-all",
                      active
                        ? "border-white/35 bg-white/[0.08] text-white"
                        : "border-white/10 bg-black/20 text-white/55 hover:border-white/20 hover:text-white",
                      readOnly && "opacity-40"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <FieldLabel label="Font size" hint="Use values like 14, 16, 20, or 28." />
              <Input
                type="number"
                min={10}
                max={72}
                value={fontSize}
                disabled={readOnly}
                onChange={(value) =>
                  onChange(
                    updateStyle(
                      element,
                      "fontSize",
                      Number.isFinite(Number(value)) ? Number(value) : 16
                    )
                  )
                }
              />
            </div>

            <div>
              <FieldLabel label="Font weight" />
              <Select
                value={fontWeight}
                disabled={readOnly}
                onChange={(value) =>
                  onChange(updateStyle(element, "fontWeight", value))
                }
              >
                {FONT_WEIGHT_OPTIONS.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    className="bg-[#111]"
                  >
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Color"
          description="Set text color and an optional block surface color."
          icon={Palette}
        >
          <div className="space-y-4">
            <div>
              <FieldLabel label="Text color" />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  disabled={readOnly}
                  value={color || "#111827"}
                  onChange={(e) => onChange(updateStyle(element, "color", e.target.value))}
                  className="h-9 w-14 cursor-pointer rounded-lg border border-white/15 bg-transparent p-0.5 disabled:opacity-40"
                />
                <Input
                  value={color}
                  disabled={readOnly}
                  onChange={(value) => onChange(updateStyle(element, "color", value))}
                  placeholder="#111827"
                />
                {color && (
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => onChange(updateStyle(element, "color", ""))}
                    className="shrink-0 rounded-lg border border-white/10 px-2.5 py-2 text-xs text-white/50 hover:text-white/80 disabled:opacity-40"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            <div>
              <FieldLabel label="Background color" hint="Leave blank for transparent." />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  disabled={readOnly}
                  value={backgroundColor || "#ffffff"}
                  onChange={(e) => onChange(updateStyle(element, "backgroundColor", e.target.value))}
                  className="h-9 w-14 cursor-pointer rounded-lg border border-white/15 bg-transparent p-0.5 disabled:opacity-40"
                />
                <Input
                  value={backgroundColor}
                  disabled={readOnly}
                  onChange={(value) => onChange(updateStyle(element, "backgroundColor", value))}
                  placeholder="transparent"
                />
                {backgroundColor && (
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => onChange(updateStyle(element, "backgroundColor", ""))}
                    className="shrink-0 rounded-lg border border-white/10 px-2.5 py-2 text-xs text-white/50 hover:text-white/80 disabled:opacity-40"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Spacing"
          description="Adjust how much vertical breathing room this block gets."
          icon={MoveVertical}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <FieldLabel label="Padding top" hint="Extra space above the content." />
              <Input
                type="number"
                min={0}
                max={200}
                value={paddingTop}
                disabled={readOnly}
                onChange={(value) =>
                  onChange(
                    updateStyle(
                      element,
                      "paddingTop",
                      Number.isFinite(Number(value)) ? Number(value) : 0
                    )
                  )
                }
              />
            </div>

            <div>
              <FieldLabel
                label="Padding bottom"
                hint="Extra space below the content."
              />
              <Input
                type="number"
                min={0}
                max={200}
                value={paddingBottom}
                disabled={readOnly}
                onChange={(value) =>
                  onChange(
                    updateStyle(
                      element,
                      "paddingBottom",
                      Number.isFinite(Number(value)) ? Number(value) : 0
                    )
                  )
                }
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Shape"
          description="Control how soft or sharp the block corners feel."
          icon={Radius}
        >
          <div>
            <FieldLabel
              label="Corner radius"
              hint="Use lower values for sharper blocks and higher values for softer cards."
            />
            <Input
              type="number"
              min={0}
              max={48}
              value={borderRadius}
              disabled={readOnly}
              onChange={(value) =>
                onChange(
                  updateStyle(
                    element,
                    "borderRadius",
                    Number.isFinite(Number(value)) ? Number(value) : 16
                  )
                )
              }
            />
          </div>
        </SectionCard>
      </div>
    </div>
  );
}